import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Legislation } from "@/models/legislation/Legislation";
import type { LegislationPdfLink } from "@/models/legislation/LegislationPdfLink";
import { restError } from "@/models/client/RestResponse";
import { PATH_KEYS } from "@/services/appServices";
import {
  type LegislationPdfSource,
  resolveLegislationPdfSource,
} from "@/pages/legislation/utils/buildPdfUrl";
import { useItemService } from "./useServices";

/** A signed link is renewed this long before it expires. */
export const PDF_LINK_RENEWAL_MARGIN_MS = 5 * 60 * 1000;

/** Never renew more often than this, even when a link is already due. */
const MIN_RENEWAL_INTERVAL_MS = 30 * 1000;

/**
 * React-query schedules staleness with a timer, which fires at once for a
 * delay past 2^31 ms; links live 30 minutes, so a day is a safe ceiling.
 */
const MAX_TIMER_DELAY_MS = 24 * 60 * 60 * 1000;

/**
 * Milliseconds until a signed link is due for renewal: 0 once it is within
 * {@link PDF_LINK_RENEWAL_MARGIN_MS} of expiring, or when there is no link.
 */
export const msUntilPdfLinkRenewal = (
  link: LegislationPdfLink | undefined,
  now: number = Date.now()
): number => {
  if (!link) return 0;
  const expiresAt = Date.parse(link.expiresOn);
  if (Number.isNaN(expiresAt)) return 0;
  const untilRenewal = expiresAt - now - PDF_LINK_RENEWAL_MARGIN_MS;
  return Math.min(Math.max(0, untilRenewal), MAX_TIMER_DELAY_MS);
};

export const legislationPdfLinkQueryKey = (
  externalId: string,
  datasource: string,
  path: string | null
) => [PATH_KEYS.legislation, "pdfLink", externalId, datasource, path] as const;

export type UseLegislationPdfUrlOptions = {
  /**
   * Keep renewing the signed link while mounted, for a control that opens it
   * later (the download button). Leave off where the URL is loaded once (the
   * PDF viewer): a renewed link is a new URL, which would reload the document.
   */
  keepFresh?: boolean;
  /** Log a warning when the legislation has no PDF at all (dedicated PDF routes only). */
  logWhenUnavailable?: boolean;
};

export type LegislationPdfUrl = {
  /** The URL to load; null while it is being signed, when signing failed, or when there is no PDF. */
  url: string | null;
  source: LegislationPdfSource | null;
  /** A stored PDF's link is still being signed. */
  isPending: boolean;
  /** A stored PDF's link could not be signed. */
  isError: boolean;
};

/**
 * The URL a legislation's PDF loads from (LAR-542). A PDF stored in the
 * data-sources account is read through a short-lived link the API signs, since
 * the account refuses anonymous reads; a PDF on another host (EUR-Lex,
 * Retsinformation) is used as it is, with no request.
 *
 * A failed signing is logged by `QueryCache.onError` with this query's key,
 * which carries the external id, datasource and path (LAR-520).
 */
const useLegislationPdfUrl = (
  legislation: Legislation | null | undefined,
  options: UseLegislationPdfUrlOptions = {}
): LegislationPdfUrl => {
  const { keepFresh = false, logWhenUnavailable = false } = options;
  const legislationService = useItemService((services) => services.legislation);

  const source = useMemo(
    () =>
      legislation
        ? resolveLegislationPdfSource(legislation, { logWhenUnavailable })
        : null,
    [legislation, logWhenUnavailable]
  );

  const storagePath = source?.kind === "storage" ? source.path : null;
  // The row's own values, which the API matches exactly.
  const externalId = legislation?.externalId ?? "";
  const datasource = legislation?.datasource ?? "";
  const enabled =
    storagePath !== null && externalId !== "" && datasource !== "";

  const linkQuery = useQuery<LegislationPdfLink>({
    queryKey: legislationPdfLinkQueryKey(externalId, datasource, storagePath),
    queryFn: async ({ signal }) => {
      const response = await legislationService.getPdfLink(
        externalId,
        datasource,
        storagePath ?? "",
        signal
      );
      if (response.success) return response.value;
      throw restError(response);
    },
    enabled,
    // Fresh until the link is due for renewal, so a remount reuses a link that
    // still works and replaces one that is about to stop.
    staleTime: (query) => msUntilPdfLinkRenewal(query.state.data),
    refetchInterval: keepFresh
      ? (query) =>
          query.state.data
            ? Math.max(
                msUntilPdfLinkRenewal(query.state.data),
                MIN_RENEWAL_INTERVAL_MS
              )
            : false
      : false,
    // The interval does not run in a hidden tab; renew on return instead.
    refetchOnWindowFocus: keepFresh,
    refetchOnReconnect: keepFresh,
  });

  if (!source) {
    return { url: null, source, isPending: false, isError: false };
  }

  if (source.kind === "url") {
    return { url: source.url, source, isPending: false, isError: false };
  }

  const link = linkQuery.data;
  return {
    url: link?.url ?? null,
    source,
    isPending: enabled && !link && linkQuery.isPending,
    isError: !link && linkQuery.isError,
  };
};

export default useLegislationPdfUrl;
